/**
 * THE ONE DRAG IMPLEMENTATION — C2-8.
 *
 * Decision recJGcud8CmXe2hNw item C2-8: "ONE implementation (pointerdown/move/up
 * + setPointerCapture) used by rows, columns AND the existing choice reorder,
 * replacing HTML5 drag-and-drop everywhere."
 *
 * =============================================================================
 * WHY THIS FILE EXISTS AT ALL, WHICH IS FOUR ATTEMPTS' WORTH OF HISTORY
 * =============================================================================
 *
 * The choice reorder took four attempts across three deploys on 2026-08-28, and
 * every one of the first three found a REAL defect in HTML5 drag-and-drop that
 * was not the last one:
 *
 *   1. `dragstart` fired, `dragover` fired, and NO `drop` ever came, because the
 *      droppable area was the rows and Matt released just above them.
 *   2. The drop still did nothing, because a drag carrying no `dataTransfer`
 *      data is a drag the browser refuses to complete.
 *   3. Grabbing the obvious thing — the name — began a TEXT drag the browser
 *      handles itself, so `onDragStart` never ran.
 *   4. The list persisted correctly and re-rendered from the server, so it
 *      looked like nothing had happened and the natural retry undid it.
 *
 * Pointer events have none of those failure modes BY CONSTRUCTION. There is no
 * drop target, so nothing can be missed; there is no dataTransfer, so nothing
 * can be empty; there is no browser-native text drag to lose the gesture to;
 * and `setPointerCapture` means the events keep arriving wherever the pointer
 * goes, including outside the window. It also fixes P7 finding 1 — the drag
 * ghost that shrinks on pick-up — by not having a browser-drawn ghost at all,
 * and it works with touch for the same reason rather than as a separate path.
 *
 * =============================================================================
 * THE GEOMETRY IS CAPTURED ONCE AND NEVER RE-READ. THIS IS THE WHOLE DESIGN.
 * =============================================================================
 *
 * Matt found the reason with three choices on screen: work the target out from
 * "which element is under the pointer" and the moment a neighbour slides aside
 * it is no longer under the pointer, so the target flips back, so it slides
 * home, so it IS under the pointer again. With three rows that oscillation is
 * the whole list; with ten it is a wobble at one boundary, which is why it
 * "seemed to fix itself" when he added more.
 *
 * So `begin()` measures every item ONCE, while the list is at rest, and every
 * answer after that is arithmetic on numbers that cannot move underneath us.
 *
 * SIZES ARE PER ITEM, not one row height. Rows are uniform and columns are not,
 * and a single step size would make column dragging progressively wronger the
 * further you went. The measured list handles both without a second code path.
 *
 * =============================================================================
 * SENSITIVITY
 * =============================================================================
 *
 * How far you must drag before a neighbour steps aside, as a FRACTION OF THAT
 * NEIGHBOUR'S size. Matt, 2026-08-28: "the sliding-out-of-the-way animation only
 * works once the item has been dragged completely on top of it" — that was
 * literally true, and 0.35 is the number that fixed it. Below about 0.25 the
 * list twitches on the small movements a hand makes while holding still, which
 * reads as the UI being nervous.
 *
 * C2-13 makes this a per-person setting (`drag_sensitivity`), which is
 * REQ-1's origin: a preference living in source is a preference only the
 * developer has. C2-13 is phase C2-D; until it ships this is the default and
 * the hook already takes an override so that landing it is a prop, not a
 * rewrite.
 */
import { useCallback, useRef, useState } from 'react';

export const DRAG_SENSITIVITY = 0.35;

/**
 * How far the pointer must travel before a press becomes a DRAG rather than a
 * click. Load-bearing for columns: the column header is both the drag handle and
 * the sort control, so without a threshold every sort click would begin a
 * one-pixel drag and the header would stop sorting.
 */
export const DRAG_THRESHOLD_PX = 4;

/**
 * EDGE AUTO-SCROLL — Matt, 2026-10-02: "dragging a column header does NOT
 * scroll the grid sideways when the pointer reaches the edge. I have to drop,
 * scroll, re-grab, repeat." So while a drag is armed, a pointer within
 * AUTOSCROLL_EDGE_PX of the visible area's edge scrolls it that way, faster the
 * closer it gets, and KEEPS scrolling while the hand is held still in the zone —
 * which is why it runs on animation frames and not on pointermove, which stops
 * the moment the pointer does.
 *
 * The geometry rule above still holds: nothing is re-measured. Scrolling moves
 * every item by the same amount, so the item under a still pointer is the one
 * at (pointer + how far the box has scrolled since the press), and that sum is
 * what indexAt is handed every frame.
 */
export const AUTOSCROLL_EDGE_PX = 60;
/** Pixels per frame at the very edge (and beyond it): ~1300px a second at 60fps. */
export const AUTOSCROLL_MAX_PX = 22;

/**
 * How far to scroll this frame, signed: negative towards the start, positive
 * towards the end, 0 in the middle. `lo`/`hi` are the visible area's inner
 * edges on the drag axis — `lo` already moved past anything frozen over the
 * start (the pinned first column, the sticky header row), because an edge zone
 * hidden under the frozen pane is a zone nobody can find.
 *
 * Linear ramp: 1px a frame at the zone's inner boundary, the maximum at the
 * edge and anywhere past it (a pointer over the frozen column, or outside the
 * box, still means "keep going that way"). On a narrow screen the zone shrinks
 * to a third of the visible span, so the two zones can never meet.
 */
export function edgeScrollSpeed(
  pos: number,
  lo: number,
  hi: number,
  edge: number = AUTOSCROLL_EDGE_PX,
  max: number = AUTOSCROLL_MAX_PX,
): number {
  const span = hi - lo;
  if (!(span > 0)) return 0;
  const zone = Math.min(edge, span / 3);
  if (pos < lo + zone) {
    const depth = Math.min(1, (lo + zone - pos) / zone);
    return -Math.max(1, Math.round(max * depth));
  }
  if (pos > hi - zone) {
    const depth = Math.min(1, (pos - (hi - zone)) / zone);
    return Math.max(1, Math.round(max * depth));
  }
  return 0;
}

export interface ReorderState {
  /** The index being dragged, once the threshold has been crossed. */
  from: number;
  /** The slot it would land in if released now. */
  over: number;
}

interface Geometry {
  /** Pointer position on the axis when the press began. */
  start: number;
  /** Centre of each item along the axis, measured once, at rest. */
  sizes: number[];
  from: number;
}

/**
 * A pointer-driven reorder over a list of DOM elements.
 *
 * The hook owns the gesture and reports WHICH SLOT the item would land in. It
 * draws nothing: rows slide, columns show an insertion line, and the choice list
 * keeps the animation Matt tuned — three presentations, one set of numbers.
 */
export function usePointerReorder(opts: {
  axis: 'x' | 'y';
  sensitivity?: number;
  /** Called on release, only when the slot actually changed. */
  onDrop: (from: number, to: number) => void;
  /**
   * The box that scrolls along the drag axis, and how much of its START edge is
   * covered by something frozen (the pinned first column, the sticky header).
   * Both are read once, at the press. Leave it out and nothing auto-scrolls.
   */
  autoScroll?: {
    scroller: () => HTMLElement | null;
    startInset?: () => number;
  };
}) {
  const { axis, onDrop } = opts;
  const autoScroll = opts.autoScroll;
  const sensitivity = opts.sensitivity ?? DRAG_SENSITIVITY;

  const [state, setState] = useState<ReorderState | null>(null);
  const geom = useRef<Geometry | null>(null);
  /** Set once the threshold is crossed. Until then the press may still be a click. */
  const armed = useRef(false);
  /**
   * THE LIVE GESTURE, IN A REF AND NOT IN STATE, and this is a bug that was
   * found by driving it with a real mouse rather than by reading it.
   *
   * The first version committed the drop from `state`. React batches, so a quick
   * gesture — every pointermove and the pointerup arriving in one task, which is
   * exactly what a fast flick and what a synthetic drag both produce — reaches
   * the release with no re-render having happened yet. `state` was still null,
   * the commit path took its early return, and the drag did nothing at all: no
   * request, no error, no sign on screen. The four-attempt failure mode, one
   * layer up.
   *
   * So the numbers the commit needs live in refs, which are current the instant
   * they are written, and `state` exists only to make the list move.
   */
  const live = useRef<ReorderState | null>(null);
  /**
   * Ends whatever the current gesture started outside React: the window
   * listeners and the auto-scroll frame loop. Set by begin(), run by finish(),
   * so a drop, a cancel and Escape all stop the scrolling in the same place.
   */
  const teardown = useRef<(() => void) | null>(null);

  const indexAt = useCallback((pos: number): number => {
    const g = geom.current;
    if (!g) return -1;
    const delta = pos - g.start;
    let t = g.from;
    if (delta > 0) {
      // Forward: reaching slot t+1 costs `sensitivity` of the NEXT item; reaching
      // t+2 costs all of item t+1 plus `sensitivity` of t+2. With uniform sizes
      // this is exactly the formula the choice list has used since 2026-08-28.
      let travelled = 0;
      while (t + 1 < g.sizes.length && delta - travelled >= (g.sizes[t + 1] ?? 0) * sensitivity) {
        travelled += g.sizes[t + 1] ?? 0;
        t += 1;
      }
    } else if (delta < 0) {
      let travelled = 0;
      while (t - 1 >= 0 && -delta - travelled >= (g.sizes[t - 1] ?? 0) * sensitivity) {
        travelled += g.sizes[t - 1] ?? 0;
        t -= 1;
      }
    }
    return t;
  }, [sensitivity]);

  const finish = useCallback((commit: boolean, pos: number) => {
    const g = geom.current;
    const wasDragging = live.current !== null;
    const to = commit && g ? indexAt(pos) : -1;
    teardown.current?.();
    teardown.current = null;
    geom.current = null;
    live.current = null;
    armed.current = false;
    setState(null);
    document.body.classList.remove('dragging-now');
    if (!commit || !g || !wasDragging) return;
    if (to >= 0 && to !== g.from) onDrop(g.from, to);
  }, [indexAt, onDrop]);

  /**
   * Attach to the HANDLE's onPointerDown. `items` is read once, here — pass the
   * live DOM nodes in display order.
   */
  const begin = useCallback((
    e: React.PointerEvent,
    index: number,
    items: readonly (HTMLElement | null | undefined)[],
  ) => {
    // Left button (or touch/pen) only. A right-click that began a drag would
    // leave the list captured with the context menu open on top of it.
    if (e.button !== 0) return;
    const measured = items.map((el) => {
      const r = el?.getBoundingClientRect();
      return (axis === 'y' ? r?.height : r?.width) ?? 0;
    });
    if (measured.length === 0 || index < 0 || index >= measured.length) return;

    const startPos = axis === 'y' ? e.clientY : e.clientX;
    geom.current = { start: startPos, sizes: measured, from: index };
    armed.current = false;

    const el = e.currentTarget as HTMLElement;

    // THE SCROLL BOX, read once like everything else. `scrolled()` is how far it
    // has moved since the press; adding it to the pointer gives the position the
    // pointer would have had over a list that never moved, which is exactly the
    // coordinate the at-rest geometry was measured in.
    const scroller = autoScroll?.scroller() ?? null;
    const inset = scroller ? Math.max(0, autoScroll?.startInset?.() ?? 0) : 0;
    const scrollPos = (): number =>
      scroller ? (axis === 'y' ? scroller.scrollTop : scroller.scrollLeft) : 0;
    const scroll0 = scrollPos();
    /** The pointer's last position on the axis, in viewport pixels. */
    let client = startPos;
    const effective = (): number => client + (scrollPos() - scroll0);

    const report = () => {
      const g = geom.current;
      if (!g) return;
      const over = indexAt(effective());
      // The ref FIRST, and unconditionally: it is what the release reads.
      live.current = { from: g.from, over };
      setState((s) => (s && s.over === over ? s : { from: g.from, over }));
    };

    let frame = 0;
    const tick = () => {
      frame = 0;
      if (!geom.current || !armed.current || !scroller) return;
      // A pointer that has left the window is not asking for anything. With
      // pointer capture the moves keep coming from outside, so this is checked
      // here rather than trusted to a pointerleave that never fires.
      const extent = axis === 'y' ? window.innerHeight : window.innerWidth;
      let speed = 0;
      if (client >= 0 && client <= extent) {
        const r = scroller.getBoundingClientRect();
        const lo = (axis === 'y' ? r.top : r.left) + inset;
        const hi = axis === 'y' ? r.bottom : r.right;
        speed = edgeScrollSpeed(client, lo, hi);
      }
      if (speed !== 0) {
        const before = scrollPos();
        if (axis === 'y') scroller.scrollTop = before + speed;
        else scroller.scrollLeft = before + speed;
        // At the bounds the browser clamps and nothing moves — no new target to
        // report, and nothing more to do this frame.
        if (scrollPos() !== before) report();
      }
      frame = window.requestAnimationFrame(tick);
    };

    const onMove = (ev: PointerEvent) => {
      const g = geom.current;
      if (!g) return;
      client = axis === 'y' ? ev.clientY : ev.clientX;
      if (!armed.current) {
        if (Math.abs(client - g.start) < DRAG_THRESHOLD_PX) return;
        armed.current = true;
        // CAPTURE ONLY ONCE IT IS REALLY A DRAG. Capturing on pointerdown would
        // swallow the click that follows a press with no movement, which on a
        // column header is the sort.
        try { el.setPointerCapture(ev.pointerId); } catch { /* already gone */ }
        // Stops the browser selecting text across the table while a drag is in
        // flight — the one thing pointer events do not remove for free.
        document.body.classList.add('dragging-now');
        live.current = { from: g.from, over: g.from };
        setState(live.current);
        if (scroller && !frame) frame = window.requestAnimationFrame(tick);
      }
      report();
    };
    const onUp = (ev: PointerEvent) => {
      client = axis === 'y' ? ev.clientY : ev.clientX;
      finish(armed.current, effective());
    };
    const onCancel = () => { finish(false, 0); };
    // ESCAPE PUTS IT BACK. Nothing is committed and the scrolling stops at once;
    // the box stays where it scrolled to, which is where the person was looking.
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      ev.preventDefault();
      finish(false, 0);
    };

    teardown.current = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey, true);
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
    };

    // ON THE WINDOW, NOT ON THE ELEMENT. setPointerCapture would be enough on
    // its own for a captured pointer, but the threshold means there is a window
    // — before the capture — where the pointer can leave the handle and the
    // gesture would be lost. Listening on the window covers both halves.
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey, true);
  }, [axis, autoScroll, finish, indexAt]);

  /**
   * Which way, if at all, the item at `index` should step aside to open the gap.
   *
   * Dragging towards the end: everything between the old slot and the target
   * moves back one. Dragging towards the start: everything between moves
   * forward one. The dragged item itself does not move — it is under the
   * pointer, where the cursor already says it is.
   */
  const shiftFor = useCallback((index: number): -1 | 0 | 1 => {
    // The RENDERED state, not the ref: this decides what is painted, and
    // painting from a value the render does not know about would be a lie.
    const s = state;
    if (!s || s.from === s.over || index === s.from) return 0;
    if (s.from < s.over && index > s.from && index <= s.over) return -1;
    if (s.from > s.over && index >= s.over && index < s.from) return 1;
    return 0;
  }, [state]);

  return { state, begin, shiftFor };
}

/** Move one item of `ids` from `from` to `to`, returning a new array. */
export function reorder<T>(ids: readonly T[], from: number, to: number): T[] {
  const out = ids.slice();
  const [moved] = out.splice(from, 1);
  if (moved === undefined) return ids.slice();
  out.splice(to, 0, moved);
  return out;
}
